import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../components/common/SafeIcon';
import Navbar from '../components/nav/Navbar';
import { useReleaseCalendar } from '../hooks/useReleaseCalendar';
import LoadingScreen from '../components/common/LoadingScreen';

const { FiCalendar, FiBell, FiMusic, FiLoader, FiClock } = FiIcons;

const getCountdown = (dateStr) => {
  const diff = new Date(dateStr) - new Date();
  if (diff <= 0) return { days: 0, hours: 0, minutes: 0, past: true };
  return {
    days:    Math.floor(diff / 86400000),
    hours:   Math.floor((diff % 86400000) / 3600000),
    minutes: Math.floor((diff % 3600000) / 60000),
    past: false,
  };
};

const CountUnit = ({ value, label }) => (
  <div className="text-center">
    <div className="text-2xl md:text-3xl font-black text-white tabular-nums">{String(value).padStart(2, '0')}</div>
    <div className="text-[9px] font-black uppercase tracking-widest text-zinc-600 mt-0.5">{label}</div>
  </div>
);

const UpcomingCard = ({ drop }) => {
  const [notified, setNotified] = useState(false);
  const countdown = getCountdown(drop.scheduled_at);

  return (
    <div className="glass-card rounded-3xl overflow-hidden group hover:-translate-y-1 transition-all duration-300">
      <div className="relative aspect-video overflow-hidden">
        {drop.cover_url
          ? <img src={drop.cover_url} alt={drop.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 opacity-70" />
          : <div className="w-full h-full bg-zinc-900 flex items-center justify-center">
              <SafeIcon icon={FiMusic} className="text-4xl text-zinc-700" />
            </div>
        }
        <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/20 to-transparent" />
        {drop.tags?.map(tag => (
          <span key={tag} className="absolute top-4 left-4 px-2 py-1 bg-zinc-950/80 backdrop-blur text-[10px] font-black tracking-widest text-brand-pink border border-brand-pink/30 rounded-lg">{tag}</span>
        ))}
        <div className="absolute bottom-4 left-4 right-4">
          <p className="text-[10px] font-black uppercase tracking-widest text-zinc-400 mb-1">{drop.artist?.name}</p>
          <h3 className="text-xl font-black uppercase tracking-tight text-white">{drop.title}</h3>
        </div>
      </div>

      <div className="p-6 space-y-5">
        <div className="flex items-center gap-2 text-zinc-500">
          <SafeIcon icon={FiCalendar} className="text-brand-pink text-sm" />
          <span className="text-xs font-black uppercase tracking-widest">
            {new Date(drop.scheduled_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
          </span>
        </div>

        {!countdown.past && (
          <div className="flex items-end gap-3 justify-center py-3 glass-subtle rounded-2xl px-4">
            <CountUnit value={countdown.days} label="Days" />
            <span className="text-2xl font-black text-zinc-700 mb-3">:</span>
            <CountUnit value={countdown.hours} label="Hrs" />
            <span className="text-2xl font-black text-zinc-700 mb-3">:</span>
            <CountUnit value={countdown.minutes} label="Min" />
          </div>
        )}

        <button onClick={() => setNotified(n => !n)}
          className={`w-full py-3 rounded-2xl text-xs font-black uppercase tracking-widest flex items-center justify-center gap-2 transition-all ${
            notified
              ? 'bg-brand-pink/10 text-brand-pink border border-brand-pink/30'
              : 'glass-card text-zinc-400 hover:text-white hover:border-brand-pink/20'
          }`}>
          <SafeIcon icon={FiBell} className="text-sm" />
          {notified ? 'Notifying You ✓' : 'Notify Me'}
        </button>
      </div>
    </div>
  );
};

const ReleaseCalendar = () => {
  const { upcoming, loading, error } = useReleaseCalendar();

  const grouped = useMemo(() => {
    const map = {};
    upcoming.forEach(drop => {
      const month = new Date(drop.scheduled_at).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
      if (!map[month]) map[month] = [];
      map[month].push(drop);
    });
    return map;
  }, [upcoming]);

  if (loading) return <LoadingScreen message="Loading calendar…" />;

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      <Navbar />
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="ambient-orb-pink  w-[600px] h-[600px] top-[-15%] right-[-10%] animate-caustic" />
        <div className="ambient-orb-coral w-[400px] h-[400px] bottom-[-10%] left-[-5%]  animate-caustic" style={{ animationDelay: '4s' }} />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 pt-32 pb-24 space-y-16">
        <div>
          <span className="text-xs font-black tracking-widest text-brand-pink uppercase">Upcoming</span>
          <h1 className="text-5xl md:text-7xl font-black uppercase tracking-tighter text-white mt-1">Release Calendar</h1>
          <p className="text-zinc-500 mt-3 font-bold text-sm max-w-xl">
            Scheduled drops from verified artists. Enable notifications to be alerted the moment they go live.
          </p>
        </div>

        {error && (
          <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-2xl">
            <p className="text-red-400 text-xs font-black">{error}</p>
          </div>
        )}

        {!error && Object.keys(grouped).length === 0 && (
          <div className="flex flex-col items-center gap-6 py-24 text-center">
            <div className="w-20 h-20 rounded-3xl glass-card flex items-center justify-center">
              <SafeIcon icon={FiClock} className="text-3xl text-zinc-700" />
            </div>
            <div>
              <p className="text-white font-black text-xl uppercase">No Upcoming Drops</p>
              <p className="text-zinc-600 text-sm font-bold mt-1">Check back soon — artists are planning their next releases.</p>
            </div>
            <Link to="/discover" className="px-6 py-3 btn-brand rounded-2xl text-xs">Browse Live Drops</Link>
          </div>
        )}

        {Object.entries(grouped).map(([month, drops]) => (
          <section key={month} className="space-y-8">
            <div className="flex items-center gap-4">
              <h2 className="text-2xl font-black uppercase tracking-tight text-white">{month}</h2>
              <div className="flex-1 h-px bg-white/5" />
              <span className="text-xs font-black uppercase tracking-widest text-zinc-600">
                {drops.length} drop{drops.length !== 1 ? 's' : ''}
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {drops.map(drop => <UpcomingCard key={drop.id} drop={drop} />)}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
};

export default ReleaseCalendar;